import React, { useState } from 'react';
import { observable, toJS } from 'mobx';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useIntl, FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';

import {
    CalculatorStepAccordion,
    CalculationPanel,
    CalculationLineBlock,
    Question,
    HiddenField,
    FormComputed,
    MessageBanner,
    MessageBannerVariants,
    MessagePropType,
    createOptionMessageResolver,
    useForm,
    Sizes,
    Locales,
    Languages,
    Colours,
    useCalculator,
    FormValue,
} from '@ratehub/base-ui';
import {
    PAYMENT_FREQUENCIES,
    INTENDED_USE,
} from '@ratehub/documents/mtg-journey-document/stable';
import {
    calculateCMHCInsurance,
    AMORTIZATION_OPTIONS_EXTENDED,
    AMORTIZATION_TERM_MESSAGES,
    PAYMENT_FREQUENCY_MESSAGES,
    MORTGAGE_PLACEHOLDERS,
    SCENARIO_TYPES,
    DEFAULT_ALL_PURCHASE_PARAMS,
    roundDownPaymentPercentage,
    getPaymentFrequencyAndDivisor,
    useApplyForRateCallback,
    RateSelectorCard,
    DownPaymentInput,
} from '@ratehub/mtg-common';
import { can_mortgage_payment_purchase } from '@ratehub/mortgage-calculations';

import useMortgageRecommendedRate from '../hooks/useMortgageRecommendedRate';
import CalculatorResultsBox from './CalculatorResultsBox';


function AffordabilityStepMortgageScenario({
    title,
    stepNumber,
    onToggleExpand,
    isProductReferralDisabled,
    ...otherProps
}) {
    const intl = useIntl();
    const localState = useLocalState();
    const form = useForm();

    return (
        <CalculatorStepAccordion
            title={title}
            stepNumber={stepNumber}
            showBadge
            onToggleExpand={onToggleExpand}
            canBeDisabled
            {...otherProps}
        >
            <FormComputed
                name="newMortgagePayment"
                value={calculateMortgagePayment}
            />

            <FormComputed
                name="maxMortgagePayment"
                value={calculateMaxMortgagePayment}
            />

            <HiddenField
                name="rateType"
                value={localState.rateFilters?.type}
            />

            <HiddenField
                name="rateTerm"
                value={localState.rateFilters?.term}
            />

            <HiddenField
                name="propertyType"
                value={localState.rateFilters?.propertyType}
            />

            <div className="inlineQuestion">
                <Question
                    field={{
                        name: 'homePrice',
                        type: 'float',
                        value: form.values?.maxAffordability,
                        validators: [
                            NOT_GREATER_THAN_MAX_AFFORDABILITY_VALIDATOR,
                        ],
                    }}
                    type="currency"
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepMortgageScenario.homePriceLabel"
                            defaultMessage="Home price"
                        />
                    )}
                    placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                />

                <FormValue>
                    {({ homePrice }) => (
                        <Question
                            field={{
                                name: 'downPayment',
                                value: {
                                    dollars: form.values?.estimatedDownPayment,
                                },
                            }}
                            type={DownPaymentInput}
                            purchasePrice={homePrice}
                            labelMessage={(
                                <FormattedMessage
                                    id="mtg-calculators.AffordabilityStepMortgageScenario.downPaymentLabel"
                                    defaultMessage="Down payment"
                                />
                            )}
                            isInline
                        />
                    )}
                </FormValue>
            </div>

            <CalculationPanel
                className="calcPanel"
                title={(
                    <FormattedMessage
                        id="mtg-calculators.AffordabilityStepMortgageScenario.calculationPanelTitle"
                        defaultMessage="Determining your total mortgage required"
                    />
                )}
            >
                <CalculationLineBlock formulas={FORMULAS} />
            </CalculationPanel>

            <div className="inlineQuestion">
                <Question
                    field={{
                        name: 'amortization',
                        type: 'choice',
                        value: localState.rateFilters?.amortization,
                        options: AMORTIZATION_OPTIONS_EXTENDED,
                    }}
                    type="dropdown"
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepMortgageScenario.amortizationLabel"
                            defaultMessage="Amortization"
                        />
                    )}
                    renderOptionLabel={createAmortizationOptionResolver(
                        intl,
                    )}
                    onChange={(value) => localState.updateFilterValues({ amortization: value })}
                />

                <Question
                    field={{
                        name: 'paymentFrequency',
                        type: 'choice',
                        value: localState.paymentFrequency,
                        options: Object.values(PAYMENT_FREQUENCIES),
                    }}
                    type="dropdown"
                    labelMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepMortgageScenario.paymentFrequencyLabel"
                            defaultMessage="Payment frequency"
                        />
                    )}
                    renderOptionLabel={createOptionMessageResolver(
                        intl,
                        Object.values(PAYMENT_FREQUENCY_MESSAGES),
                        'Mortgages.paymentFrequencies.',
                    )}
                    onChange={(value) => localState.savePaymentFrequency(value)}
                />
            </div>

            <div className="rateContainer">
                <Choose>
                    <When condition={isProductReferralDisabled}>
                        <FormComputed
                            name="selectedRate"
                            // always treated as a custom rate
                            value={values => values.rateValue ? { value: values.rateValue } : null}
                            dependencies={[]}
                        />

                        <Question
                            field={{
                                name: 'rateValue',
                                type: 'float',
                            }}
                            type="percent"
                            labelMessage={(
                                <FormattedMessage
                                    id="mtg-calculators.affordability-calculator.rateValue.label"
                                    defaultMessage="Mortgage rate"
                                />
                            )}
                            placeholder={(
                                <FormattedMessage
                                    id="mtg-calculators.affordability-calculator.rateValue.placeholder"
                                    defaultMessage="Rate (%)"
                                />
                            )}
                        />
                    </When>
                    <Otherwise>
                        <p className="rateSelectorLabel rh-text-l weight-medium">
                            <FormattedMessage
                                id="mtg-calculators.AffordabilityStepMortgageScenario.mortgageRateLabel"
                                defaultMessage="Mortgage rate"
                            />
                        </p>

                        <HiddenField
                            name="selectedRate"
                        />

                        <RateSelectorCard
                            selectedRateName="selectedRate"
                            rateFetchingOptions={localState.rateFilters}
                            onFilterChange={localState.updateFilterValues}
                            className="rh-mb-1"
                        />
                    </Otherwise>
                </Choose>
            </div>

            <If
                condition={
                    localState.selectedRate?.provider
                    && localState.isRateConfirmed
                }
            >
                <MessageBanner
                    className="messageBanner"
                    variant={MessageBannerVariants.SUCCESS}
                    iconKey="light-bulb"
                    message={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepMortgageScenario.messageBannerMessage"
                            defaultMessage="Secure your rate by talking to a broker"
                        />
                    )}
                    messageSize="normal"
                    ctaMessage={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepMortgageScenario.messageBannerCTAMessage"
                            defaultMessage="Get this rate"
                        />
                    )}
                    ctaDataName="mtg-affordabilityCalculator-getRate"
                    onClick={localState.openLeadForm}
                />
            </If>

            <CalculatorResultsBox
                resultValue={localState.form.values.newMortgagePayment}
                resultMessage={(
                    <FormattedMessage
                        id="mtg-calculator.AffordabilityStepMortgageScenario.mortgagePaymentResultLabel"
                        defaultMessage="Your mortgage payment"
                    />
                )}
            />
        </CalculatorStepAccordion>
    );
}

AffordabilityStepMortgageScenario.propTypes = {
    title: MessagePropType.isRequired,
    stepNumber: PropTypes.number.isRequired,
    onToggleExpand: PropTypes.func,
    isProductReferralDisabled: PropTypes.bool,
};

AffordabilityStepMortgageScenario.defaultProps = {
    onToggleExpand: undefined,
    isProductReferralDisabled: undefined,
};

function calculateMortgagePayment({
    homePrice,
    downPayment,
    selectedRate,
    paymentFrequency,
    amortization,
}) {
    if (!homePrice || !downPayment?.percent || !selectedRate) {
        return;
    }

    const { frequency, divisor } = getPaymentFrequencyAndDivisor(paymentFrequency);

    const payment = can_mortgage_payment_purchase(
        homePrice,
        downPayment.percent / 100,
        selectedRate?.value / 100,
        parseInt(amortization),
        frequency,
        divisor,
    );

    return payment;
}

function calculateMaxMortgagePayment({
    maxAffordability,
    estimatedDownPayment, // TODO: replace with a common downPayment when the calculator can support using one downPaymentDollar form field
    selectedRate,
    paymentFrequency,
    amortization,
}) {
    if (!estimatedDownPayment || !maxAffordability) {
        return null;
    }

    // TODO: Updated for a hotfix in FIT-3075. Replace with a downPaymentPercent form field.
    const downPaymentPercent = roundDownPaymentPercentage(estimatedDownPayment / maxAffordability * 100);

    return calculateMortgagePayment({
        homePrice: maxAffordability,
        downPayment: { percent: downPaymentPercent },
        selectedRate,
        paymentFrequency,
        amortization,
    });
}

function useLocalState() {
    const form = useForm();
    const calculator = useCalculator();
    const { locale } = useIntl();
    const recommendedRateState = useMortgageRecommendedRate();
    const applyForRate = useApplyForRateCallback();

    const [ state ] = useState(() =>
        observable({
            form: form,
            locale: locale,

            isRateConfirmed: true, // rate initially selected
            isModalOpen: false,

            /**
             * Needed to prevent passing invalid values to rate selector card;
             * If a value is invalid, we want to pass it as null to signal we shouldn't attempt to fetch rates.
             * Only when all values are non-null, we will do a fetch.
             *
             * @param {string} name
             * @returns valid value or null
             */
            getFormValue(name) {
                const field = form.getFieldNamed(name);

                // It might be computed or the field isn't in the form (on first render).
                if (!field) {
                    return form.getValue(name);
                }

                return field.isValid ? field.typedValue : null;
            },

            get isFetching() {
                return recommendedRateState.isFetching;
            },
            get shouldShowBanner() {
                return state.selectedRate;
            },
            get shouldShowModal() {
                return state.isModalOpen;
            },
            get canInquireRate() {
                // Can only inquire on rates from the API
                return state.selectedRate?.provider;
            },
            get selectedRate() {
                return state.form.values.selectedRate;
            },
            get recommendedRate() {
                return recommendedRateState.recommendedRate;
            },
            get languageCode() {
                return state.locale === Locales.FRENCH
                    ? Languages.FRENCH
                    : Languages.ENGLISH;
            },
            get rateFilters() {
                const mortgage = state.savedCalculatorData?.mortgages?.[0];
                const property = state.savedCalculatorData?.property;

                // if document is loaded, we want to load the saved values
                // therefore the order of accessing the filter values is:
                // document > form values > default values
                return {
                    ...DEFAULT_ALL_PURCHASE_PARAMS,
                    scenario: SCENARIO_TYPES.PURCHASE,
                    language: state.languageCode,
                    city: getCityObjectFromCityProvincePair(property?.address)
                        ?? state.getFormValue('futureHomeLocation')
                        ?? DEFAULT_ALL_PURCHASE_PARAMS.city,
                    // home price is calculated from step #1
                    homePrice: state.getFormValue('homePrice') ?? DEFAULT_ALL_PURCHASE_PARAMS.homePrice,
                    downPayment: state.getFormValue('downPayment')?.percent != null
                        ? { percent: state.getFormValue('downPayment.percent') / 100 }
                        : { percent: DEFAULT_ALL_PURCHASE_PARAMS.downPaymentPercent },
                    amortization: mortgage?.amortization
                        ? mortgage.amortization / 12
                        : state.getFormValue('amortization')
                            ? parseInt(state.getFormValue('amortization'))
                            : DEFAULT_ALL_PURCHASE_PARAMS.amortization,
                    term: mortgage?.term
                        ?? (state.getFormValue('rateTerm') && parseInt(state.getFormValue('rateTerm')))
                        ?? DEFAULT_ALL_PURCHASE_PARAMS.term,
                    type: mortgage?.type
                        ?? state.getFormValue('rateType')
                        ?? DEFAULT_ALL_PURCHASE_PARAMS.type,
                    intendedUse: property?.intendedUse
                        ?? state.getFormValue('propertyType')
                        ?? INTENDED_USE.OWNER_OCCUPIED,
                };
            },
            get isDocumentLoaded() {
                return state.document !== null;
            },
            get savedCalculatorData() {
                return toJS(state.document?.journey?.calculators);
            },
            get paymentFrequency() {
                return state.savedCalculatorData?.mortgage?.paymentFrequencyType ?? PAYMENT_FREQUENCIES.MONTHLY;
            },
            get document() {
                return calculator.document;
            },
            updateFilterValues(values) {
                const { type, term, intendedUse } = state.rateFilters;

                const data = {
                    type: values?.type ?? type,
                    term: values?.term ?? term,
                    intendedUse: values?.intendedUse ?? intendedUse,
                };

                form.setValue('rateType', data.type);
                form.setValue('rateTerm', data.term);
                form.setValue('propertyType', data.intendedUse);

                state.document?.journey?.update({
                    calculators: {
                        mortgage: {
                            type: data.type,
                            term: data.term,
                        },
                        property: {
                            intendedUse: data.intendedUse,
                        },
                    },
                });
            },
            openLeadForm() {
                const formValues = state.form.values;

                applyForRate({
                    rate: state.selectedRate,
                    rateFetchingOptions: {
                        homePrice: formValues?.homePrice,
                        downPaymentPercent: formValues?.downPaymentPercent
                            ? formValues.downPaymentPercent / 100
                            : DEFAULT_ALL_PURCHASE_PARAMS.downPaymentPercent,
                        amortization: formValues?.amortization
                            ? parseInt(formValues.amortization)
                            : DEFAULT_ALL_PURCHASE_PARAMS.amortization,
                        term: formValues?.rateTerm,
                        type: formValues?.rateType,
                        city: formValues?.futureHomeLocation,
                        scenario: SCENARIO_TYPES.PURCHASE,
                    },
                });
            },
            savePaymentFrequency(value) {
                state.document?.journey?.update({
                    calculators: {
                        mortgage: {
                            paymentFrequencyType: value,
                        },
                    },
                });
            },
        }),
    );

    return state;
}

function getCityObjectFromCityProvincePair({ city, province } = {}) {
    if (!city || !province) {
        return null;
    }

    return { name: city, province: { code: province } };
}

function createAmortizationOptionResolver({ formatMessage }) {
    return term =>
        formatMessage(AMORTIZATION_TERM_MESSAGES.TERM_MESSAGE, { term });
}

const FORMULAS = [
    {
        id: 'homePriceCalculation',
        name: 'homePriceCalculation',
        label: (
            <FormattedMessage
                id="mtg-calculators.AffordabilityStepMortgageScenario.homePriceCalculationLabel"
                defaultMessage="Home price"
            />
        ),
        operandValue: 'homePrice',
    },
    {
        id: 'downPaymentCalculation',
        name: 'downPaymentCalculation',
        label: (
            <FormattedMessage
                id="mtg-calculators.AffordabilityStepMortgageScenario.downPaymentCalculationLabel"
                defaultMessage="Down payment"
            />
        ),
        operandValue: 'downPayment.dollars',
        operator: 'minus',
    },
    {
        id: 'cmhcCalculation',
        name: 'cmhcCalculation',
        label: (
            <FormattedMessage
                id="mtg-calculators.AffordabilityStepMortgageScenario.cmhcCalculationLabel"
                defaultMessage="CMHC Insurance"
            />
        ),
        operandValue: ({ homePrice, downPayment, amortization }) => {
            if (homePrice && downPayment?.dollars && amortization) {
                const amortizationInMonths = parseInt(amortization) * 12;
                return calculateCMHCInsurance(
                    homePrice,
                    downPayment.dollars,
                    amortizationInMonths,
                );
            }

            return 0;
        },
        operator: 'plus',
    },
    {
        id: 'mortgageRequiredCalculation',
        name: 'mortgageRequiredCalculation',
        label: (
            <FormattedMessage
                id="mtg-calculators.AffordabilityStepMortgageScenario.mortgageRequiredLabel"
                defaultMessage="Total mortgage required"
            />
        ),
        operandValue: ({ homePrice, downPayment, cmhcCalculation }) => {
            if (homePrice && downPayment?.dollars) {
                return homePrice + cmhcCalculation - downPayment.dollars;
            }

            return 0;
        },
        operator: 'equals',
        result: true,
    },
];

const StyledAffordabilityStepMortgageScenario = styled(
    observer(AffordabilityStepMortgageScenario),
)`
    .inlineQuestion {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));

        margin: 0 -${Sizes.SPACING.ONE};
        margin-top: -${Sizes.SPACING.TWO};
        row-gap: ${Sizes.SPACING.ONE};

        [data-question-name] {
            margin: 0 ${Sizes.SPACING.ONE};
        }

        .homePriceQuestion {
            margin: 0 ${Sizes.SPACING.ONE};
            margin-top: ${Sizes.SPACING.TWO};
        }

        .downpaymentQuestion {
            margin-top: 0;
        }
    }

    .customizedLabel {
        margin-top: -${Sizes.SPACING.ONE};
        margin-bottom: ${Sizes.SPACING.HALF};
    }

    .calcPanel {
        margin-bottom: ${Sizes.SPACING.TWO};
    }

    .rateContainer {
        position: relative;

        > .rateSelectorLabel {
            margin: 0 0 ${Sizes.SPACING.HALF};
        }

        > .recommendedRateCard[data-active='true'] {
            border: 1px solid ${Colours.BLUEBERRY_DARK};
            flex-wrap: wrap;
        }
    }

    .comparisonTable {
        margin: ${Sizes.SPACING.ONE} 0;
    }

    .messageBanner {
        padding-top: 0;
        padding-bottom: 0;
    }
`;

const NOT_GREATER_THAN_MAX_AFFORDABILITY_VALIDATOR = {
    fn: (value, { maxAffordability }) => value <= maxAffordability,
    message: (
        <FormattedMessage
            id="mtg-calculators.AffordabilityMortgageScenario.Validators.maxCharacterValidator"
            defaultMessage="Your home price should not exceeds max affordability"
        />
    ),
};

export default StyledAffordabilityStepMortgageScenario;
